import type { ReactNode } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    DataGridListOption,
    DataGridListPickerEdit,
} from "../../../columns/listEditors";

type UsePickerOptions<TData, TOption extends string | number> = NonNullable<
    DataGridListPickerEdit<TData, TOption>["useOptions"]
>;

/** A single picker's first row, which commits `null`. */
export interface PickerEmptyOption {
    label: string;
    leading?: ReactNode;
    onSelect: () => void;
}

const OptionItems = <TOption extends string | number>({
    options,
}: Readonly<{ options: ReadonlyArray<DataGridListOption<TOption>> }>) =>
    options.length === 0 ? (
        <Picker.Empty />
    ) : (
        options.map((option) => (
            <Picker.Item
                key={option.value}
                value={option.value}
                leading={option.leading}
                description={option.description}
                disabled={option.disabled}
                disabledReason={option.disabledReason}
            >
                {option.label}
            </Picker.Item>
        ))
    );

/** Runs the column's `useOptions` hook while the editor is open. */
const AsyncOptionItems = <TData, TOption extends string | number>({
    row,
    useOptions,
    search,
}: Readonly<{
    row: TData;
    useOptions: UsePickerOptions<TData, TOption>;
    search: string;
}>) => {
    // oxlint-disable-next-line react/hooks -- intended skip: each column's picker factory makes its own editor component, so a mount always calls that column's one hook
    const { options, status } = useOptions(row, search);
    if (status === "loading") {
        return <Picker.Loading>Loading…</Picker.Loading>;
    }
    if (status === "error") {
        return <Picker.Error>Failed to load options</Picker.Error>;
    }
    return <OptionItems options={options} />;
};

interface PickerListOptionsProps<TData, TOption extends string | number> {
    row: TData;
    edit: DataGridListPickerEdit<TData, TOption>;
    /** The open editor's search text, fed to `useOptions`. */
    search: string;
    emptyOption: PickerEmptyOption | undefined;
}

/**
 * A picker editor's popup body: the search bar for `useOptions`, then the
 * empty option and the row's options, or the loading or error row. Mounted
 * only while the editor is open, so `useOptions` runs for one cell at a time.
 */
export const PickerListOptions = <TData, TOption extends string | number>({
    row,
    edit,
    search,
    emptyOption,
}: Readonly<PickerListOptionsProps<TData, TOption>>) => (
    <>
        {edit.useOptions !== undefined && <Picker.Search />}
        <Picker.List>
            {emptyOption !== undefined && (
                <Picker.Item
                    leading={emptyOption.leading}
                    onSelect={emptyOption.onSelect}
                >
                    {emptyOption.label}
                </Picker.Item>
            )}
            {edit.useOptions === undefined ? (
                <OptionItems options={edit.options(row)} />
            ) : (
                <AsyncOptionItems
                    row={row}
                    useOptions={edit.useOptions}
                    search={search}
                />
            )}
        </Picker.List>
    </>
);
